
import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { Product } from 'types'
import { useMemo } from 'react'

interface ProductsResponse {
  data: Product[]
  total: number
  limit: number
  offset: number
}

type UseProductsParams = {
  limit?: number
  offset?: number
  company_id?: string
  includeArchived?: boolean
  // Owners → Products reference filter (VRG-80).
  ownerId?: string
}

type UseProductsResult = {
  data: ProductsResponse | null
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

export const useProducts = (params: UseProductsParams = {}, options?: { enabled?: boolean }): UseProductsResult => {
  // Create stable params to prevent infinite loops
  const stableParams = useMemo(() => ({
    limit: params.limit ?? 50, // Use nullish coalescing to be more explicit
    offset: params.offset ?? 0,
    ...params,
  }), [params.limit, params.offset, params.company_id, params.includeArchived, params.ownerId])

  // Only enable the query when we have valid parameters and it's not explicitly disabled
  const isEnabled = options?.enabled !== false && stableParams.limit > 0

  const query = useQuery({
    queryKey: ['products', 'list', stableParams],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/products/admin/company',
        params: stableParams,
      })
      return response.data as ProductsResponse
    },
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    // Prevent background refetches when component remounts
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    // Keep previous data while loading new data to prevent flicker
    placeholderData: (previousData) => previousData,
    // Ensure data persists across re-renders
    gcTime: 1000 * 60 * 10, // 10 minutes garbage collection
  })



  return {
    data: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
